import { useCallback, useMemo } from 'react';

import { isSameLanguage } from 'src/data/generation';
import { GENERATION_TOOL } from 'src/data/generationTool';
import { useAnalyzerToneOfVoiceSelectionPopup } from 'src/views/AnalyzerToneOfVoiceSelectionPopup';
import { useBrandVoicePopup } from 'src/views/BrandVoicePopup';
import { useToneOfVoicePopup } from 'src/views/ToneOfVoicePopup';

import { useAnalyzerTonesOfVoice } from '../useAnalyzerTonesOfVoice';
import { useBrandVoices } from '../useBrandVoices';
import { PAGES } from '../useTrackingEvent';

export const useToneOfVoiceProps = ({
  toneOfVoiceInsightsData,
  generationTool,
  applyingAnalyzedToneOfVoice,
  handleApplyAnalyzedToneOfVoice,
  disabled,
  loading,
  language,
}) => {
  const { analyzerToneOfVoice, analyzerTonesOfVoice } = useAnalyzerTonesOfVoice({
    generationTool,
    language,
  });
  const openAnalyzerToneOfVoiceSelectionPopup = useAnalyzerToneOfVoiceSelectionPopup();
  const openBrandVoicePopup = useBrandVoicePopup();
  const openToneOfVoicePopup = useToneOfVoicePopup();
  const { brandVoices } = useBrandVoices();

  const brandVoiceExistForLanguage =
    brandVoices.filter((brandVoice) => isSameLanguage(brandVoice.language, language)).length > 0;

  const handleSettingsClick = useCallback(() => {
    openAnalyzerToneOfVoiceSelectionPopup(generationTool, language);
  }, [openAnalyzerToneOfVoiceSelectionPopup, generationTool, language]);

  const handleOpenToneOfVoicePopup = useCallback(
    ({ toneOfVoice, options }) => {
      openToneOfVoicePopup({
        page: PAGES.editor,
        toneOfVoice,
        ...options,
        language,
      });
    },
    [openToneOfVoicePopup, language]
  );

  const handleEmptyTovClick = useCallback(
    async ({ toneOfVoice, options }) => {
      if (brandVoiceExistForLanguage) {
        handleOpenToneOfVoicePopup({ toneOfVoice, options });
      } else {
        const newBrandVoice = await openBrandVoicePopup(null, true, language);
        if (!newBrandVoice) return;
        handleOpenToneOfVoicePopup({ toneOfVoice, options });
      }
    },
    [language, openBrandVoicePopup, brandVoiceExistForLanguage, handleOpenToneOfVoicePopup]
  );

  return useMemo(() => {
    if (generationTool === GENERATION_TOOL.googleRSA) {
      // google RSA can't be tone of voice analyzed
      return null;
    }
    return {
      onSettingsClick: handleSettingsClick,
      toneOfVoiceInsights: toneOfVoiceInsightsData?.items,
      displayBlogEmptyState: toneOfVoiceInsightsData?.displayBlogEmptyState,
      analyzerToneOfVoice,
      onEmptyTovClick: handleEmptyTovClick,
      availableTovCount: analyzerTonesOfVoice.length,
      onApplyToneOfVoice: handleApplyAnalyzedToneOfVoice,
      applyingAnalyzedToneOfVoice,
      loading,
      disabled,
      brandVoiceExistForLanguage,
      language,
    };
  }, [
    generationTool,
    handleSettingsClick,
    toneOfVoiceInsightsData?.items,
    toneOfVoiceInsightsData?.displayBlogEmptyState,
    applyingAnalyzedToneOfVoice,
    handleApplyAnalyzedToneOfVoice,
    analyzerToneOfVoice,
    handleEmptyTovClick,
    analyzerTonesOfVoice,
    loading,
    disabled,
    brandVoiceExistForLanguage,
    language,
  ]);
};
